import { Menu } from '@mantine/core';
import {
    IconDotsVertical,
    IconExternalLink,
    IconFolderShare,
    IconRefresh,
    IconTrash,
} from '@tabler/icons-react';
import type { ContainerResponse } from '@xver/apis/v1/kufer/containers/model';
import { NewTabLink } from '@xver/components/Atoms';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { moveTriggerProps, useMoveResource } from '@xver/hooks/useMoveResource';
import { formatDate } from '@xver/utils/date';
import { ProjectName } from '@xver/components/Molecules';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import ContainerAvatar from './ContainerAvatar';
import ContainerStatusBadge from './ContainerStatusBadge';

interface ContainerListTableProps {
    containers: ContainerResponse[] | undefined;
    isLoading: boolean;
    /** False with view-only access: the row actions column is not rendered. */
    canManage: boolean;
    onRestart: (container: ContainerResponse) => void;
    onDelete: (container: ContainerResponse) => void;
    isMutationPending?: boolean;
    emptyMessage?: string;
}

export default function ContainerListTable({
    containers,
    isLoading,
    canManage,
    onRestart,
    onDelete,
    isMutationPending,
    emptyMessage,
}: ContainerListTableProps) {
    const { t, i18n } = useTranslation(['containerEngine', 'common']);
    const navigate = useNavigate();
    const { canMove, requestMove, moveModal } = useMoveResource('container');

    const columns: DataTableColumn<ContainerResponse>[] = [
        {
            key: 'name',
            label: <span className="text-nowrap">{t('table.name')}</span>,
            render: (c) => (
                <div className="flex flex-row items-center gap-3">
                    <ContainerAvatar name={c.name} status={c.status} />
                    <div className="flex flex-col text-base text-nowrap gap-1">
                        <div className="flex flex-row items-center gap-2">
                            <Link
                                to={`/container-engine/${c.id}`}
                                className="font-bold text-fg hover:underline"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {c.name}
                            </Link>
                            <ContainerStatusBadge status={c.status} />
                        </div>
                        {c.source?.type && (
                            <span className="text-fg/68">
                                {c.source.type === 'github'
                                    ? 'GitHub source'
                                    : c.source.type === 'url'
                                      ? `Image: ${c.source.image}:${c.source.image_tag ?? 'latest'}`
                                      : 'Inline Dockerfile'}
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: 'project',
            label: <span className="text-nowrap">{t('projects:switcher.label')}</span>,
            render: (c) => <ProjectName projectId={c.xver_project_id} />,
        },
        {
            key: 'endpoint',
            label: <span className="text-nowrap">{t('table.endpoint')}</span>,
            render: (c) => {
                const url = c.endpoint?.domain || c.endpoint?.default;
                if (!url) {
                    return <span className="text-xs text-fg/68">-</span>;
                }
                return (
                    <NewTabLink
                        href={url}
                        className="inline-flex items-center gap-1 text-base  text-blue-400 light:text-blue-700 hover:text-blue-300 light:hover:text-blue-800 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconExternalLink size={12} aria-hidden="true" />
                        <span className="truncate max-w-65">{url.replace(/^https?:\/\//, '')}</span>
                    </NewTabLink>
                );
            },
        },
        {
            key: 'pricing',
            label: <span className="text-nowrap">{t('table.pricing')}</span>,
            render: (c) => (
                <span className="text-base text-nowrap text-fg/70">
                    {c.pricing_type === 'request_based' ? 'Request-based' : 'Instance-based'}
                </span>
            ),
        },
        {
            key: 'updated',
            label: <span className="text-nowrap">{t('table.lastDeploy')}</span>,
            render: (c) => (
                <span className="text-base text-nowrap text-fg/68">
                    {c.last_deployed_at
                        ? formatDate(c.last_deployed_at, i18n.language, t)
                        : formatDate(c.created_at, i18n.language, t)}
                </span>
            ),
        },
    ];

    return (
        <>
            <DataTable
                columns={columns}
                rows={containers}
                isLoading={isLoading}
                rowKey={(c) => c.id}
                onRowClick={(c) => navigate(`/container-engine/${c.id}`)}
                emptyMessage={emptyMessage ?? t('empty.containers')}
                caption={t('tabs.containers')}
                actionsLabel={t('projects:common.actions')}
                actions={
                    canManage
                        ? (c) => (
                              <Menu position="bottom-end" withinPortal>
                                  <Menu.Target>
                                      <button
                                          {...moveTriggerProps('container', c.id)}
                                          type="button"
                                          className="p-1 rounded hover:bg-fg/8 transition-colors"
                                          aria-label={t('table.rowActions', { name: c.name })}
                                          onClick={(e) => e.stopPropagation()}
                                      >
                                          <IconDotsVertical
                                              size={16}
                                              stroke={1.5}
                                              color="var(--fg)"
                                              aria-hidden="true"
                                          />
                                      </button>
                                  </Menu.Target>
                                  <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                                      <Menu.Item
                                          leftSection={<IconRefresh size={16} stroke={1.5} />}
                                          onClick={() => onRestart(c)}
                                          disabled={isMutationPending}
                                      >
                                          {t('actions.restart')}
                                      </Menu.Item>
                                      {canMove && (
                                          <Menu.Item
                                              leftSection={
                                                  <IconFolderShare size={16} stroke={1.5} />
                                              }
                                              onClick={() =>
                                                  requestMove({ id: c.id, name: c.name })
                                              }
                                          >
                                              {t('projects:resource.moveToProject')}
                                          </Menu.Item>
                                      )}
                                      <Menu.Divider />
                                      <Menu.Item
                                          color="var(--color-status-danger)"
                                          leftSection={<IconTrash size={16} stroke={1.5} />}
                                          onClick={() => onDelete(c)}
                                          disabled={isMutationPending}
                                      >
                                          {t('actions.delete')}
                                      </Menu.Item>
                                  </Menu.Dropdown>
                              </Menu>
                          )
                        : undefined
                }
            />

            {moveModal}
        </>
    );
}
